import * as React from 'react';
import { Web3ReactProvider } from '@web3-react/core';
import { initializeConnector } from '@web3-react/core';
import { MetaMask } from '@web3-react/metamask';
import { CoinbaseWallet } from '@web3-react/coinbase-wallet';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { memerTheme } from './theme/memerTheme';
import { ApolloProvider } from '@apollo/client';

// Import Rainbow Kit and Wagmi providers
import { WagmiProvider } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RainbowKitProvider } from '@rainbow-me/rainbowkit';
import { config } from './config/wagmi';
import { foundry, base, baseSepolia } from 'wagmi/chains';

// Import Rainbow Kit styles
import '@rainbow-me/rainbowkit/styles.css';

// Import the new context providers
import { SignatureProvider } from './components/context/SignatureContext';
import { TransactionProvider } from './components/context/TransactionContext';
import { WagmiWalletProvider } from './components/context/WagmiWalletContext';
import { ContractProvider } from './components/context/ContractContext';
import { TabStateProvider } from './components/context/TabStateContext';
import { Auth0Provider } from './components/context/Auth0Context';

// Import the authenticated app wrapper and Auth0 config loader
import AuthenticatedApp from './components/auth/AuthenticatedApp';
import Auth0ConfigLoader from './components/auth/Auth0ConfigLoader';
import ErrorBoundary from './components/ErrorBoundary';

// Import Apollo client conditionally
import { createApolloClient } from './graphql/client';
import { ApolloClient, InMemoryCache, HttpLink } from '@apollo/client';
import logger from './utils/logger';

// RPC URLs configuration (using environment variables for security)
const RPC_URLS: Record<number, string> = {
    31337: 'http://localhost:8545',
    8453: import.meta.env.VITE_BASE_MAINNET_RPC_URL || '',
    84532: import.meta.env.VITE_BASE_SEPOLIA_RPC_URL || ''
};

// Auth0 configuration is now handled dynamically by Auth0ConfigLoader
// No more hardcoded VITE environment variables needed

// Initialize connectors outside of the component to ensure they are referentially static
const [metaMask, metaMaskHooks] = initializeConnector<MetaMask>(
    (actions) => new MetaMask({ actions })
);

const [coinbaseWallet, coinbaseWalletHooks] = initializeConnector<CoinbaseWallet>(
    (actions) => new CoinbaseWallet({
        actions,
        options: {
            appName: 'Memer MultiSig Wallet',
            url: 'http://localhost:3000'
        }
    })
);

// Create a static connectors array with proper typing - moved outside component
const web3ReactConnectors = [
    [metaMask, metaMaskHooks] as [typeof metaMask, typeof metaMaskHooks],
    [coinbaseWallet, coinbaseWalletHooks] as [typeof coinbaseWallet, typeof coinbaseWalletHooks]
];

// Create a client for React Query - moved outside component for stability
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 5, // 5 minutes
            gcTime: 1000 * 60 * 10, // 10 minutes
        },
    },
});

// Create a fallback Apollo client for when subgraphs are disabled
const createFallbackApolloClient = () => {
    logger.info('🚫 Creating fallback Apollo client (subgraphs disabled)');
    return new ApolloClient({
        link: new HttpLink({ uri: 'http://localhost:0' }), // Invalid URL to prevent requests
        cache: new InMemoryCache(),
    });
};

function MyApp() {
    // Get chain ID from environment variable (supports Base mainnet 8453, Anvil local 31337, etc.)
    const chainId = parseInt(import.meta.env.VITE_CHAIN_ID || '8453');

    // Determine initial chain based on environment variable
    const getInitialChain = () => {
        switch (chainId) {
            case 8453:
                return base;
            case 84532:
                return baseSepolia;
            case 31337:
                return foundry;
            default:
                // Default to Base mainnet for production safety
                return base;
        }
    };

    const initialChain = getInitialChain();
    const [client, setClient] = React.useState(() => {
        // Initialize with fallback client if subgraphs are disabled
        if (import.meta.env.VITE_DISABLE_SUBGRAPHS === 'true') {
            return createFallbackApolloClient();
        }
        // Otherwise create a minimal client that will be replaced
        return new ApolloClient({
            link: new HttpLink({ uri: 'http://localhost:0' }),
            cache: new InMemoryCache(),
        });
    });

    // Initialize Apollo client with the correct chain ID
    React.useEffect(() => {
        const initApolloClient = async () => {
            if (import.meta.env.VITE_DISABLE_SUBGRAPHS === 'true') {
                logger.info('🚫 MyApp: Subgraphs disabled, using fallback client');
                setClient(createFallbackApolloClient());
                return;
            }

            const newClient = await createApolloClient(chainId.toString());
            // If subgraphs are disabled, newClient will be null
            if (newClient) {
                setClient(newClient);
            } else {
                logger.info('🚫 MyApp: Subgraphs disabled, using fallback client');
                setClient(createFallbackApolloClient());
            }
        };

        initApolloClient();
    }, [chainId]);

    // DEBUG: Log MyApp initialization
    React.useEffect(() => {
        const subgraphsDisabled = import.meta.env.VITE_DISABLE_SUBGRAPHS === 'true';
        console.log('🔍 MyApp initialized with:', {
            chainId,
            subgraphsDisabled,
            // Debug the actual environment variable value
            VITE_DISABLE_SUBGRAPHS_RAW: import.meta.env.VITE_DISABLE_SUBGRAPHS
        });
    }, []);

    return (
        <ErrorBoundary>
            <Auth0ConfigLoader>
                <ThemeProvider theme={memerTheme}>
                    <CssBaseline />
                    <Auth0Provider>
                        <WagmiProvider config={config}>
                            <QueryClientProvider client={queryClient}>
                                <RainbowKitProvider
                                    initialChain={initialChain}
                                    showRecentTransactions={true}
                                    modalSize="compact"
                                    coolMode={true}
                                >
                                    <WagmiWalletProvider>
                                        <ApolloProvider client={client}>
                                            <Web3ReactProvider connectors={web3ReactConnectors} key="web3-react-provider">
                                                <ContractProvider>
                                                    <TabStateProvider>
                                                        <SignatureProvider>
                                                            <TransactionProvider>
                                                                <AuthenticatedApp />
                                                            </TransactionProvider>
                                                        </SignatureProvider>
                                                    </TabStateProvider>
                                                </ContractProvider>
                                            </Web3ReactProvider>
                                        </ApolloProvider>
                                    </WagmiWalletProvider>
                                </RainbowKitProvider>
                            </QueryClientProvider>
                        </WagmiProvider>
                    </Auth0Provider>
                </ThemeProvider>
            </Auth0ConfigLoader>
        </ErrorBoundary>
    );
}

export default MyApp;
